import React, { useState, useEffect, useRef } from 'react';
import { StepHeader } from '../ui/StepHeader.jsx';
import { StepIntro } from '../ui/StepIntro.jsx';
import { TrackmobPrivacyPolicy } from '@trackmob/landing-components/legal';
import { Icon } from '../ui/Icon.jsx';
import { maskCpf, maskCnpj, maskDocument, maskDate, isValidCpf, isValidCnpj, isValidDocument, isValidPhone, isValidBirthDate, onlyDigits } from '../utils/masks.js';
import { IS_DEV } from '../utils/env.js';
import { PhoneField } from '../ui/PhoneField.jsx';
import { FormError } from '../ui/FormError.jsx';
import { getVocabulary } from '../utils/vocabulary.js';
import { getFormCustomFields, maskCustomFieldValue, customFieldMaxLength, validateCustomField, isCustomFieldFilled } from '../utils/customFields.js';

const DATA_POLICY_URL = 'https://trackmob.com.br/legal/politica-de-tratamento-de-dados/';
const DEFAULT_PRIVACY_CONTACT_EMAIL = 'politica@trackmob.com.br';
const DEFAULT_ORGANIZATION_NAME = 'organização';
const COMMUNICATION_SCOPE_TEXT = 'Você receberá comunicações sobre o projeto atendido, informações relacionadas a doação e seu processamento de pagamento, eventuais inconsistências cadastrais e prestação de contas da organização.';

// Fields that share a row when they appear next to each other in the form.
const HALF_FIELDS = new Set(['Document', 'Phone']);

const FIELD_ORDER = ['Name', 'Email', 'Document', 'ResponsibleName', 'Phone', 'BirthDate'];

const CPF_ONLY = { label: 'CPF', placeholder: 'CPF', maxLength: 14, mask: maskCpf, isValid: isValidCpf, error: 'CPF inválido' };
const CNPJ_ONLY = { label: 'CNPJ', placeholder: 'CNPJ', maxLength: 18, mask: maskCnpj, isValid: isValidCnpj, error: 'CNPJ inválido' };
const CPF_OR_CNPJ = { label: 'Documento', placeholder: 'CPF ou CNPJ', maxLength: 18, mask: maskDocument, isValid: isValidDocument, error: 'CPF/CNPJ inválido' };

function isCompanyDonor(personTypes, document) {
  const onlyCnpj = Array.isArray(personTypes) && personTypes.length === 1 && personTypes[0] === 'Cnpj';
  return onlyCnpj || onlyDigits(document).length > 11;
}

function isFieldShown(field, isCompany) {
  if (field.Enabled === false) return false;
  if (field.Name === 'BirthDate') return !isCompany;
  if (field.Name === 'ResponsibleName') return isCompany;
  return true;
}

function fieldPosition(field) {
  const position = FIELD_ORDER.indexOf(field.Name);
  return position === -1 ? FIELD_ORDER.length : position;
}

function getDocumentRules(personTypes) {
  if (!Array.isArray(personTypes) || personTypes.length !== 1) return CPF_OR_CNPJ;
  return personTypes[0] === 'Cnpj' ? CNPJ_ONLY : CPF_ONLY;
}

function buildFieldRows(fields) {
  const rows = [];
  let i = 0;
  while (i < fields.length) {
    const current = fields[i];
    const next = fields[i + 1];
    if (HALF_FIELDS.has(current.Name) && next && HALF_FIELDS.has(next.Name)) {
      rows.push([current, next]);
      i += 2;
    } else {
      rows.push([current]);
      i += 1;
    }
  }
  return rows;
}

/**
 * Donor info step — config-driven fields, standard labelled inputs.
 */
export function DonorInfoStep({ config, formData, onChange, onNext, onBack, onClose, isInline, isPreview, isSubmitting, canGoBack }) {
  const { DonorFields, Goal, PersonTypes } = config.Behaviour;
  const documentRules = getDocumentRules(PersonTypes);
  const vocab = getVocabulary(config);
  const [errors, setErrors] = useState({});

  const info = formData.donorInfo || {};
  // A field is shown unless explicitly disabled. The backend config lists donor
  // fields without an `Enabled` flag (presence == shown); only the mock sets it.
  const isCompany = isCompanyDonor(PersonTypes, info.Document);
  const enabledFields = DonorFields
    .filter(field => isFieldShown(field, isCompany))
    .sort((a, b) => fieldPosition(a) - fieldPosition(b));
  const phoneCountry = info.PhoneCountry || 'BR';
  const customFields = getFormCustomFields(config);
  const customFieldValues = formData.customFieldValues || {};

  function handleChange(name, value) {
    const next = name === 'Document' ? documentRules.mask(value) : name === 'BirthDate' ? maskDate(value) : value;
    onChange({ donorInfo: { ...info, [name]: next } });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  }

  // intl-tel-input drives the phone field; the number arrives already in E.164.
  function handlePhoneChange(e164) {
    onChange({ donorInfo: { ...info, Phone: e164 } });
    if (errors.Phone) setErrors(prev => ({ ...prev, Phone: '' }));
  }

  function handleCustomFieldChange(field, value) {
    onChange({ customFieldValues: { ...customFieldValues, [field.Key]: maskCustomFieldValue(field, value) } });
    const errorKey = customFieldErrorKey(field);
    if (errors[errorKey]) setErrors(prev => ({ ...prev, [errorKey]: '' }));
  }

  function handleCustomFieldBlur(field) {
    setErrors(prev => ({ ...prev, [customFieldErrorKey(field)]: validateCustomField(field, customFieldValues[field.Key]) }));
  }

  function handlePhoneCountryChange(iso2) {
    onChange({ donorInfo: { ...info, PhoneCountry: iso2 } });
  }

  function fillFake() {
    const candidates = FAKE_DONORS.filter(donor => documentRules.isValid(donor.Document));
    const group = candidates[Math.floor(Math.random() * candidates.length)];
    const next = { ...info, PhoneCountry: 'BR', TermsAccepted: true };
    const groupIsCompany = isCompanyDonor(PersonTypes, group.Document);
    for (const field of DonorFields.filter(f => isFieldShown(f, groupIsCompany))) {
      if (field.Name === 'Phone') next.Phone = `+55${group.Phone}`;
      else if (group[field.Name] != null) next[field.Name] = group[field.Name];
    }
    onChange({ donorInfo: next });
    setErrors({});
  }

  function validateField(name) {
    const field = enabledFields.find(f => f.Name === name);
    if (!field) return '';

    const value = String(info[name] || '').trim();
    if (!value) return field.Required ? `${labelFor(field, documentRules, isCompany)} é obrigatório` : '';
    if (name === 'Email' && !isValidEmail(value)) return 'E-mail inválido';
    if (name === 'Document' && !documentRules.isValid(value)) return documentRules.error;
    if (name === 'Phone' && !isValidPhone(value, phoneCountry)) return 'Telefone inválido';
    if (name === 'BirthDate' && !isValidBirthDate(value)) return 'Data de nascimento inválida';
    return '';
  }

  function validate() {
    const errs = {};
    for (const field of enabledFields) {
      const error = validateField(field.Name);
      if (error) errs[field.Name] = error;
    }
    for (const field of customFields) {
      const error = validateCustomField(field, customFieldValues[field.Key]);
      if (error) errs[customFieldErrorKey(field)] = error;
    }
    return errs;
  }

  function handleBlur(name) {
    setErrors(prev => ({ ...prev, [name]: validateField(name) }));
  }

  async function handleNext() {
    if (!isPreview) {
      const errs = validate();
      if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    }
    await onNext();
  }

  const allRequiredFilled = enabledFields
    .filter(f => f.Required)
    .every(f => String(info[f.Name] || '').trim().length > 0)
    && customFields
      .filter(f => f.Required)
      .every(f => isCustomFieldFilled(customFieldValues[f.Key]));
  const termsAccepted = info.TermsAccepted === true;

  function renderField(field) {
    return (
      <div key={field.Name} className={`tdw-field tdw-donor-field-${field.Name}`}>
        <label className="tdw-label">
          {labelFor(field, documentRules, isCompany)}{field.Required && ' *'}
        </label>
        {field.Name === 'Phone' ? (
          <PhoneField
            country={phoneCountry}
            onCountryChange={handlePhoneCountryChange}
            value={info.Phone || ''}
            onChange={handlePhoneChange}
            error={!!errors.Phone}
          />
        ) : (
          <input
            type={field.Name === 'Email' ? 'email' : 'text'}
            className={`tdw-input${errors[field.Name] ? ' error' : ''}`}
            placeholder={placeholderFor(field, documentRules, isCompany)}
            value={info[field.Name] || ''}
            onChange={e => handleChange(field.Name, e.target.value)}
            onBlur={() => handleBlur(field.Name)}
            autoComplete={autoComplete(field.Name, isCompany)}
            inputMode={field.Name === 'Document' || field.Name === 'BirthDate' ? 'numeric' : undefined}
            maxLength={maxLengthFor(field, documentRules)}
          />
        )}
        {errors[field.Name] && (
          <p className="tdw-field-error">{errors[field.Name]}</p>
        )}
      </div>
    );
  }

  function renderCustomFieldInput(field) {
    const value = customFieldValues[field.Key] || '';
    const errorKey = customFieldErrorKey(field);
    const className = `tdw-input${errors[errorKey] ? ' error' : ''}`;

    if (field.Type === 'Select' || field.Type === 'Boolean') {
      const options = field.Type === 'Boolean'
        ? [{ value: 'true', label: 'Sim' }, { value: 'false', label: 'Não' }]
        : (field.Options || []).map(option => ({ value: option, label: option }));

      return (
        <div className="tdw-select-wrap">
          <select
            className={className}
            value={value}
            onChange={e => handleCustomFieldChange(field, e.target.value)}
            onBlur={() => handleCustomFieldBlur(field)}
          >
            <option value="">Selecionar</option>
            {options.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <span className="tdw-select-arrow"><Icon name="chevronDown" size={16} /></span>
        </div>
      );
    }

    if (field.Type === 'LongText') {
      return (
        <textarea
          className={className}
          rows={3}
          placeholder={field.Label}
          value={value}
          onChange={e => handleCustomFieldChange(field, e.target.value)}
          onBlur={() => handleCustomFieldBlur(field)}
          maxLength={customFieldMaxLength(field)}
        />
      );
    }

    return (
      <input
        type="text"
        className={className}
        placeholder={field.Type === 'Date' ? 'DD/MM/AAAA' : field.Label}
        value={value}
        onChange={e => handleCustomFieldChange(field, e.target.value)}
        onBlur={() => handleCustomFieldBlur(field)}
        inputMode={field.Type === 'Date' || field.Type === 'Number' ? 'numeric' : undefined}
        maxLength={customFieldMaxLength(field)}
      />
    );
  }

  function renderCustomField(field) {
    const errorKey = customFieldErrorKey(field);

    return (
      <div key={errorKey} className="tdw-field tdw-custom-field">
        <label className="tdw-label">
          {field.Label}{field.Required && ' *'}
        </label>
        {renderCustomFieldInput(field)}
        {errors[errorKey] && (
          <p className="tdw-field-error">{errors[errorKey]}</p>
        )}
      </div>
    );
  }

  return (
    <div className="tdw-donor-step" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <StepHeader
        onClose={onClose}
        showClose={!isInline}
        onDevFill={IS_DEV && !isPreview ? fillFake : undefined}
      />

      <div className="tdw-step-body">
        <StepIntro
          title="Seus dados"
          subtitle={Goal === 'Lead'
            ? 'Usamos seus dados para entrar em contato com você.'
            : `Usamos seus dados para o recibo e a confirmação ${vocab.ofThe} ${vocab.term}.`}
        />

        <div className="tdw-donor-fields">
          {buildFieldRows(enabledFields).map((row, rowIdx) => (
            row.length === 2
              ? <div key={rowIdx} className="tdw-field-row tdw-field-row-donor">{row.map(renderField)}</div>
              : renderField(row[0])
          ))}
          {customFields.map(renderCustomField)}
        </div>

        <div className="tdw-optin">
          <p className="tdw-optin-intro">
            Nós adoraríamos poder entrar em contato com você sobre esta e outras campanhas.{' '}
            <InfoTooltip text={COMMUNICATION_SCOPE_TEXT} />
          </p>
          <label className="tdw-checkbox-row">
            <input
              type="checkbox"
              checked={info.CommunicationOptIn === true}
              onChange={e => handleChange('CommunicationOptIn', e.target.checked)}
            />
            <span>Sim, por favor. Desejo receber essas comunicações.</span>
          </label>
        </div>

        <div className="tdw-checkbox-row tdw-terms-row">
          <input
            type="checkbox"
            id="tdw-terms"
            checked={termsAccepted}
            onChange={e => handleChange('TermsAccepted', e.target.checked)}
          />
          <span>
            Li e aceito as{' '}
            <PrivacyPolicy
              organizationName={config.OrganizationName || DEFAULT_ORGANIZATION_NAME}
              email={config.PrivacyContactEmail || DEFAULT_PRIVACY_CONTACT_EMAIL}
              organizationPolicy={config.OrganizationPrivacyPolicy}
            />
          </span>
        </div>

        <div style={{ flex: 1 }} />

        <RecaptchaDisclosure />

        <FormError message={formData.apiError} />

        <button
          type="button"
          className="tdw-btn-cta"
          onClick={handleNext}
          disabled={!isPreview && (!allRequiredFilled || !termsAccepted || isSubmitting)}
        >
          {isSubmitting ? 'Aguarde...' : 'Continuar'}
        </button>

        {canGoBack && (
          <button
            type="button"
            className="tdw-btn-link"
            onClick={onBack}
            disabled={isSubmitting}
          >
            ← Voltar
          </button>
        )}
      </div>
    </div>
  );
}

function RecaptchaDisclosure() {
  return (
    <p className="tdw-recaptcha-disclosure">
      Este site é protegido pelo reCAPTCHA. Aplicam-se a{' '}
      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Política de Privacidade</a>{' '}
      e os{' '}
      <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Termos de Serviço</a>{' '}
      do Google.
    </p>
  );
}

function InfoTooltip({ text }) {
  return (
    <span className="tdw-tooltip">
      <button type="button" className="tdw-tooltip-trigger" aria-label="Mais informações">?</button>
      <span className="tdw-tooltip-bubble" role="tooltip">{text}</span>
    </span>
  );
}

function PrivacyPolicy({ organizationName, email, organizationPolicy }) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!popoverOpen) return undefined;
    function handleDocClick(event) {
      if (ref.current && !event.composedPath().includes(ref.current)) {
        setPopoverOpen(false);
      }
    }
    document.addEventListener('click', handleDocClick);
    return () => document.removeEventListener('click', handleDocClick);
  }, [popoverOpen]);

  return (
    <span className="tdw-privacy" ref={ref}>
      <button
        type="button"
        className="tdw-inline-link"
        aria-expanded={popoverOpen}
        onClick={() => setPopoverOpen(open => !open)}
      >
        Políticas de Privacidade
      </button>
      {popoverOpen && (
        <span className="tdw-privacy-popover" role="tooltip">
          Você pode acessar a <i><b>Política de Privacidade da {organizationName}</b></i> por meio{' '}
          <button
            type="button"
            className="tdw-inline-link"
            onClick={() => { setModalOpen(true); setPopoverOpen(false); }}
          >
            deste link
          </button>. Este site foi desenvolvido pela Trackmob. Você pode acessar a{' '}
          <i><b>Política de Tratamento de Dados da Trackmob</b></i> por meio{' '}
          <a className="tdw-inline-link" href={DATA_POLICY_URL} target="_blank" rel="noreferrer">deste link</a>.
        </span>
      )}
      {modalOpen && (
        <PrivacyModal
          organizationName={organizationName}
          email={email}
          organizationPolicy={organizationPolicy}
          onClose={() => setModalOpen(false)}
        />
      )}
    </span>
  );
}

function PrivacyModal({ organizationName, email, organizationPolicy, onClose }) {
  const [activeTab, setActiveTab] = useState('trackmob');

  useEffect(() => {
    function handleKey(event) { if (event.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const hasOrganizationPolicy = Boolean(organizationPolicy?.Content || organizationPolicy?.ExternalLink);

  return (
    <div
      className="tdw-privacy-modal-overlay"
      onClick={event => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className="tdw-privacy-modal" role="dialog" aria-modal="true" aria-label="Políticas de Privacidade">
        <div className="tdw-privacy-modal-head">
          <strong>Políticas de Privacidade</strong>
          <button type="button" className="tdw-privacy-modal-close" onClick={onClose} aria-label="Fechar">
            <Icon name="close" size={18} />
          </button>
        </div>
        {hasOrganizationPolicy && (
          <div className="tdw-privacy-modal-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'trackmob'}
              className={`tdw-privacy-modal-tab${activeTab === 'trackmob' ? ' active' : ''}`}
              onClick={() => setActiveTab('trackmob')}
            >
              Trackmob
            </button>
            {organizationPolicy.ExternalLink && (
              <a
                className="tdw-privacy-modal-tab"
                href={organizationPolicy.ExternalLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {organizationName}
              </a>
            )}
            {!organizationPolicy.ExternalLink && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'organization'}
                className={`tdw-privacy-modal-tab${activeTab === 'organization' ? ' active' : ''}`}
                onClick={() => setActiveTab('organization')}
              >
                {organizationName}
              </button>
            )}
          </div>
        )}
        <div className="tdw-privacy-modal-body">
          {activeTab === 'trackmob' && <TrackmobPrivacyPolicy organizationName={organizationName} email={email} linkClassName="tdw-inline-link" />}
          {activeTab === 'organization' && (
            <div className="tdw-privacy-modal-organization" dangerouslySetInnerHTML={{ __html: organizationPolicy.Content }} />
          )}
        </div>
        <div className="tdw-privacy-modal-actions">
          <button type="button" className="tdw-btn-cta" onClick={onClose}>Entendi</button>
        </div>
      </div>
    </div>
  );
}

function isValidEmail(value) {
  if (typeof value !== 'string') {
    return false;
  }

  const email = value.trim();
  if (!email || email.includes(' ')) {
    return false;
  }

  const atIndex = email.indexOf('@');
  if (atIndex <= 0 || atIndex !== email.lastIndexOf('@')) {
    return false;
  }

  const localPart = email.slice(0, atIndex);
  const domainPart = email.slice(atIndex + 1);
  if (!localPart || !domainPart) {
    return false;
  }

  const dotIndex = domainPart.indexOf('.');
  return dotIndex > 0 && dotIndex < domainPart.length - 1;
}

function customFieldErrorKey(field) {
  return `custom:${field.Key}`;
}

function autoComplete(name, isCompany) {
  const map = {
    Name:            isCompany ? 'organization' : 'name',
    ResponsibleName: 'name',
    Email:           'email',
    Phone:           'tel',
    BirthDate:       'bday',
    Document:        'off'
  };
  return map[name] || 'off';
}

function labelFor(field, documentRules, isCompany) {
  if (field.Name === 'Document') return documentRules.label;
  if (field.Name === 'Name' && isCompany) return 'Nome da empresa';
  return field.Label;
}

function maxLengthFor(field, documentRules) {
  if (field.Name === 'Document') return documentRules.maxLength;
  if (field.Name === 'BirthDate') return 10;
  if (field.Name === 'ResponsibleName') return 100;
  return undefined;
}

// Dev fill datasets (valid CPFs/CNPJs). A random group is picked per click.
const FAKE_DONORS = [
  { Name: 'Maria Silva',          Email: 'maria.silva@example.com',     Document: '391.916.523-32',     Phone: '11991230001' },
  { Name: 'João Souza',           Email: 'joao.souza@example.com',      Document: '285.471.890-92',     Phone: '21992340002' },
  { Name: 'Ana Oliveira',         Email: 'ana.oliveira@example.com',    Document: '123.654.987-28',     Phone: '31993450003' },
  { Name: 'Pedro Santos',         Email: 'pedro.santos@example.com',    Document: '456.789.123-64',     Phone: '41994560004' },
  { Name: 'Carla Lima',           Email: 'carla.lima@example.com',      Document: '741.852.963-55',     Phone: '51995670005' },
  { Name: 'Lucas Pereira',        Email: 'lucas.pereira@example.com',   Document: '159.357.486-06',     Phone: '61996780006' },
  { Name: 'Beatriz Costa',        Email: 'beatriz.costa@example.com',   Document: '987.321.654-55',     Phone: '71997890007' },
  { Name: 'Rafael Almeida',       Email: 'rafael.almeida@example.com',  Document: '246.813.579-28',     Phone: '81998900008' },
  { Name: 'Instituto Bem Maior',  Email: 'contato@bemmaior.org',        Document: '11.222.333/0001-81', Phone: '11991110009', ResponsibleName: 'Helena Rocha' },
  { Name: 'Fundação Esperança',   Email: 'contato@esperanca.org',       Document: '45.928.377/0001-43', Phone: '11992220010', ResponsibleName: 'Marcos Teixeira' }
];

function placeholderFor(field, documentRules, isCompany) {
  if (field.Name === 'Document') return documentRules.placeholder;
  if (field.Name === 'BirthDate') return 'DD/MM/AAAA';
  return labelFor(field, documentRules, isCompany);
}
